import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Upload, FileText, CheckCircle, Clock, XCircle, ArrowLeft,
  RefreshCw, Link2, Unlink, Trash2, Eye, ChevronRight,
  CreditCard, TrendingUp, AlertCircle, Check, X, Percent
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BankReconciliationPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [matching, setMatching] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [transRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/bank/transactions?status=${filter}&limit=100`, { headers }),
        fetch(`${API_URL}/api/bank/stats`, { headers })
      ]);

      const transData = await transRes.json();
      const statsData = await statsRes.json();

      setTransactions(transData.transactions || []);
      setStats(statsData);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file || !file.name.endsWith('.csv')) {
      alert('Veuillez sélectionner un fichier CSV');
      return;
    }

    setUploading(true);
    setImportResult(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/bank/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const result = await res.json();

      if (res.ok) {
        setImportResult({
          success: true,
          imported: result.imported,
          skipped: result.skipped,
          columns: result.detected_columns
        });
        setFilter('pending');
        await loadData();
      } else {
        setImportResult({
          success: false,
          error: result.detail || 'Erreur lors de l\'import'
        });
      }
    } catch (error) {
      setImportResult({
        success: false,
        error: 'Erreur de connexion au serveur'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const loadSuggestions = async (transaction) => {
    setSelectedTransaction(transaction);
    setLoadingSuggestions(true);
    setSuggestions([]);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/bank/suggestions/${transaction.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Erreur suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const matchTransaction = async (transactionId, invoiceId) => {
    setMatching(invoiceId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/bank/match`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transaction_id: transactionId,
          invoice_id: invoiceId
        })
      });

      if (res.ok) {
        setSelectedTransaction(null);
        setSuggestions([]);
        await loadData();
      }
    } catch (error) {
      console.error('Erreur matching:', error);
    } finally {
      setMatching(null);
    }
  };

  const ignoreTransaction = async (transactionId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/bank/ignore/${transactionId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedTransaction(null);
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const unmatchTransaction = async (transactionId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/bank/unmatch/${transactionId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'bg-emerald-100 text-emerald-700';
    if (confidence >= 50) return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bank-reconciliation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/smartfacture">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Rapprochement bancaire</h1>
            <p className="text-slate-500 text-sm">Importez votre relevé CSV et rapprochez vos factures</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">À traiter</p>
                <p className="text-2xl font-bold text-amber-600" data-testid="pending-count">
                  {stats?.transactions?.pending || 0}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-amber-50">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Rapprochées</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {stats?.transactions?.matched || 0}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Montant rapproché</p>
                <p className="text-2xl font-bold text-blue-600">
                  {stats?.total_matched_amount?.toLocaleString('fr-FR')} €
                </p>
              </div>
              <div className="p-3 rounded-xl bg-blue-50">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Taux rapprochement</p>
                <p className="text-2xl font-bold text-violet-600">
                  {stats?.reconciliation_rate || 0}%
                </p>
              </div>
              <div className="p-3 rounded-xl bg-violet-50">
                <Percent className="h-5 w-5 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Import Zone */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Upload className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle>Importer un relevé bancaire</CardTitle>
              <CardDescription>Glissez-déposez votre fichier CSV ou cliquez pour sélectionner</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
              dragOver 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
            }`}
            data-testid="drop-zone"
          >
            <input
              type="file"
              accept=".csv"
              onChange={(e) => handleFileUpload(e.target.files[0])}
              className="hidden"
              id="csv-input"
            />
            <label htmlFor="csv-input" className="cursor-pointer">
              {uploading ? (
                <div className="flex flex-col items-center">
                  <RefreshCw className="h-12 w-12 text-blue-500 animate-spin mb-3" />
                  <p className="text-slate-600">Import en cours...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="p-4 rounded-full bg-blue-50 mb-3">
                    <FileText className="h-8 w-8 text-blue-500" />
                  </div>
                  <p className="font-medium text-slate-800">Glissez votre fichier CSV ici</p>
                  <p className="text-sm text-slate-500 mt-1">ou cliquez pour sélectionner</p>
                  <p className="text-xs text-slate-400 mt-3">
                    Compatible: BNP, Société Générale, Crédit Agricole, LCL, Boursorama, etc.
                  </p>
                </div>
              )}
            </label>
          </div>

          {/* Import Result */}
          {importResult && (
            <div className={`mt-4 p-4 rounded-xl ${
              importResult.success 
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {importResult.success ? (
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5" />
                  <div>
                    <p className="font-medium">{importResult.imported} transactions importées</p>
                    {importResult.skipped > 0 && (
                      <p className="text-sm opacity-80">{importResult.skipped} lignes ignorées</p>
                    )}
                    <p className="text-xs mt-1">
                      Colonnes détectées: {importResult.columns?.date}, {importResult.columns?.amount}, {importResult.columns?.label}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5" />
                  <span>{importResult.error}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <CreditCard className="h-5 w-5 text-slate-600" />
              </div>
              <CardTitle>Transactions</CardTitle>
            </div>
            
            {/* Filter Tabs */}
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
              {[
                { key: 'pending', label: 'À traiter', count: stats?.transactions?.pending },
                { key: 'matched', label: 'Rapprochées', count: stats?.transactions?.matched },
                { key: 'ignored', label: 'Ignorées', count: stats?.transactions?.ignored }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    filter === tab.key 
                      ? 'bg-white text-slate-800 shadow-sm' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="ml-1.5 text-xs">({tab.count})</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {transactions.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {transactions.map((transaction) => (
                <div 
                  key={transaction.id}
                  className={`py-4 ${
                    selectedTransaction?.id === transaction.id ? 'bg-blue-50 -mx-6 px-6 rounded-lg' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`p-2 rounded-lg ${
                        transaction.amount > 0 ? 'bg-emerald-100' : 'bg-red-100'
                      }`}>
                        {transaction.amount > 0 ? (
                          <TrendingUp className="h-5 w-5 text-emerald-600" />
                        ) : (
                          <CreditCard className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">
                          {transaction.label || 'Sans libellé'}
                        </p>
                        <p className="text-sm text-slate-500">
                          {new Date(transaction.date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <span className={`font-semibold ${
                        transaction.amount > 0 ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString('fr-FR')} €
                      </span>
                      
                      {filter === 'pending' && transaction.amount > 0 && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => loadSuggestions(transaction)}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid={`match-btn-${transaction.id}`}
                          >
                            <Link2 className="h-4 w-4 mr-1" />
                            Rapprocher
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => ignoreTransaction(transaction.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      
                      {filter === 'matched' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => unmatchTransaction(transaction.id)}
                        >
                          <Unlink className="h-4 w-4 mr-1" />
                          Annuler
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Suggestions Panel */}
                  {selectedTransaction?.id === transaction.id && (
                    <div className="mt-4 p-4 bg-white rounded-lg border border-slate-200">
                      <h4 className="font-medium text-slate-800 mb-3">Factures suggérées</h4>
                      
                      {loadingSuggestions ? (
                        <div className="flex items-center justify-center py-4">
                          <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />
                        </div>
                      ) : suggestions.length > 0 ? (
                        <div className="space-y-2">
                          {suggestions.map((sugg) => (
                            <div 
                              key={sugg.invoice_id}
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <Badge className={getConfidenceColor(sugg.confidence)}>
                                  {sugg.confidence}%
                                </Badge>
                                <div>
                                  <p className="font-medium text-slate-800">
                                    {sugg.invoice_number} - {sugg.client_name}
                                  </p>
                                  <p className="text-sm text-slate-500">
                                    {sugg.amount.toLocaleString('fr-FR')} € • {new Date(sugg.date).toLocaleDateString('fr-FR')}
                                  </p>
                                  <p className="text-xs text-blue-600 mt-1">
                                    {sugg.match_reasons.join(' • ')}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => matchTransaction(transaction.id, sugg.invoice_id)}
                                disabled={matching === sugg.invoice_id}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                {matching === sugg.invoice_id ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="h-4 w-4 mr-1" />
                                    Valider
                                  </>
                                )}
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-slate-500">Aucune facture correspondante trouvée</p>
                          <p className="text-sm text-slate-400 mt-1">
                            Vérifiez que la facture existe et n'est pas déjà payée
                          </p>
                        </div>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedTransaction(null)}
                        className="mt-3 w-full"
                      >
                        Fermer
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <CreditCard className="h-8 w-8 text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">Aucune transaction</p>
              <p className="text-sm text-slate-500 mt-1">
                {filter === 'pending' 
                  ? 'Importez un relevé CSV pour commencer'
                  : filter === 'matched'
                  ? 'Aucune transaction rapprochée'
                  : 'Aucune transaction ignorée'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-blue-100">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-800">Comment exporter votre relevé CSV ?</h3>
              <ul className="text-sm text-blue-600 mt-2 space-y-1 list-disc list-inside">
                <li>Connectez-vous à votre espace bancaire en ligne</li>
                <li>Accédez à l'historique des opérations</li>
                <li>Sélectionnez la période souhaitée</li>
                <li>Cliquez sur "Exporter" ou "Télécharger" au format CSV</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BankReconciliationPage;
